import React, { PureComponent, createRef } from 'react';
import {ButterflyChart} from 'react-butterfly-chart';
import { formatNumber } from '@telerik/kendo-intl';
import Images from "../../Assets/Images";
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import "../../Styles/TornadoChart.scss";
import EmailService from "../../Services/EmailService";
import EmailChartPopup from "../Common/EmailChartPopup";

export default class TornadoChart extends PureComponent {
  constructor(props) {
    super(props);
    // Create a reference to the chart container
    this.chartContainerRef = createRef();
    // Create a reference specifically for the ButterflyChart
    this.butterflyChartRef = createRef();
  }

  // Helper function to download chart as image
  downloadChartAsImage = async () => {
    try {
      console.log("Starting PNG export...");
      
      // Get chart title from props or use default
      const chartTitle = this.props.title || 'Tornado Chart';
      
      // Get chart data
      const { leftPopulationDataNew, rightPopulationDataNew } = this.getChartData();
      
      // Check if this is an opportunity chart
      const isOpportunityChart = this.props.isOpportunity === true;
      
      // Create a canvas element with increased width from the start
      const canvas = document.createElement('canvas');
      canvas.width = 1200; // Increased width to accommodate labels
      
      // Calculate the minimum required height based on the number of data points
      const totalDataPoints = leftPopulationDataNew.length + rightPopulationDataNew.length;
      const baseHeight = 150; // Base height for title, margins, etc.
      
      // Minimum height needed per data point to ensure readability
      // This is based on the bar height (30px) plus spacing (25px) plus extra for text wrapping
      const minHeightPerItem = 60;
      
      // Calculate the absolute minimum height needed to display all data points
      // This ensures no data truncation regardless of the number of records
      const requiredHeight = baseHeight + (totalDataPoints * minHeightPerItem);
      
      // Use either the required height or a minimum of 625px, whichever is larger
      canvas.height = Math.max(625, requiredHeight);
      
      console.log(`Canvas height set to ${canvas.height}px for ${totalDataPoints} data points`);
      const context = canvas.getContext('2d');
      
      // Set background to white for better visibility in exports
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw title
      context.fillStyle = '#000000';
      context.font = 'bold 24px Arial';
      context.textAlign = 'center';
      const updatedTitle = this.props.optionSelection == "all" ? chartTitle : chartTitle + ' - Top 10';
      context.fillText(updatedTitle, canvas.width / 2, 30);
      
      // Calculate max value for scaling
      let maxValue = 0;
      leftPopulationDataNew.forEach(item => {
        const value = Math.abs(parseFloat(item.value.replace(/,/g, '')));
        if (value > maxValue) maxValue = value;
      });
      rightPopulationDataNew.forEach(item => {
        const value = Math.abs(parseFloat(item.value.replace(/,/g, '')));
        if (value > maxValue) maxValue = value;
      });
      
      // Add some margin
      maxValue = maxValue * 1.1;
      
      // Calculate scale factor - ensure bars are visible
      const scaleX = Math.min((canvas.width / 2 - 150) / maxValue, 0.9);
      
      // Draw left data (negative values)
      const barHeight = 30;
      const startY = 80;
      let currentY = startY;
      
      // Draw center line
      context.strokeStyle = '#999999';
      context.beginPath();
      context.moveTo(canvas.width / 2, 60);
      context.lineTo(canvas.width / 2, startY + (leftPopulationDataNew.length + rightPopulationDataNew.length) * (barHeight + 10) + 20);
      context.stroke();
      
      // Helper function to wrap text
      const wrapText = (context, text, x, y, maxWidth, lineHeight) => {
        const words = text.split(' ');
        let line = '';
        let lines = [];
        
        for(let n = 0; n < words.length; n++) {
          const testLine = line + words[n] + ' ';
          const metrics = context.measureText(testLine);
          const testWidth = metrics.width;
          
          if (testWidth > maxWidth && n > 0) {
            lines.push(line);
            line = words[n] + ' ';
          } else {
            line = testLine;
          }
        }
        
        lines.push(line);
        
        for(let i = 0; i < lines.length; i++) {
          context.fillText(lines[i], x, y + (i * lineHeight));
        }
        
        return lines.length;
      };
      
      // Combine all data for consistent handling
      const allData = [...rightPopulationDataNew, ...leftPopulationDataNew];
      
      // Sort combined data by absolute value (largest first)
      const sortedData = allData.sort((a, b) => {
        const absA = Math.abs(parseFloat(a.value.replace(/,/g, '')));
        const absB = Math.abs(parseFloat(b.value.replace(/,/g, '')));
        if (absA < absB) return 1;
        if (absA > absB) return -1;
        return 0;
      });
      
      // Process each data item
      sortedData.forEach((item, index) => {
        const value = Math.abs(parseFloat(item.value.replace(/,/g, '')));
        const barWidth = value * scaleX;
        const isPositive = parseFloat(item.value.replace(/,/g, '')) >= 0;
        
        // Determine if this item should be displayed on the left or right side
        // For opportunity charts, all values (including zero) should be on the left side
        // For threat charts, negative values on left, positive values on right
        let isLeftSide;
        
        if (isOpportunityChart) {
          // In opportunity charts, all values go on the left side
          isLeftSide = true;
        } else {
          // In threat charts, negative values go on left, positive on right
          isLeftSide = !isPositive;
        }
        
        // Draw bar with appropriate color
        // Yellow (#bfbf2d) for left side, Blue (#018cf4) for right side
        context.fillStyle = isLeftSide ? '#bfbf2d' : '#018cf4';
        const visibleBarWidth = Math.max(barWidth, 5);
        
        if (!isLeftSide) {
          // Draw from center to right
          context.fillRect(canvas.width / 2, currentY, visibleBarWidth, barHeight);
          
          // Draw value on the bar
          context.fillStyle = '#000000';
          context.font = '12px Arial';
          if (barWidth > 50) {
            context.textAlign = 'right';
            context.fillText(item.value, canvas.width / 2 + barWidth - 10, currentY + barHeight / 2 + 4);
          } else {
            context.textAlign = 'left';
            context.fillText(item.value, canvas.width / 2 + barWidth + 5, currentY + barHeight / 2 + 4);
          }
        } else {
          // Draw from center to left
          context.fillRect(canvas.width / 2 - visibleBarWidth, currentY, visibleBarWidth, barHeight);
          
          // Draw value on the bar
          context.fillStyle = '#000000';
          context.font = '12px Arial';
          if (barWidth > 50) {
            context.textAlign = 'left';
            context.fillText(item.value, canvas.width / 2 - barWidth + 10, currentY + barHeight / 2 + 4);
          } else {
            context.textAlign = 'right';
            context.fillText(item.value, canvas.width / 2 - barWidth - 5, currentY + barHeight / 2 + 4);
          }
        }
        
        // Extract risk number and title if available
        let riskNumber = '';
        let riskTitle = '';
        
        if (typeof item.riskNumber === 'string' && item.riskNumber.includes(' ')) {
          const parts = item.riskNumber.split(' ');
          riskNumber = parts[0];
          riskTitle = parts.slice(1).join(' ');
        } else {
          riskNumber = item.riskNumber || 'Item ' + index;
        }
        
        // Position risk number based on which side the bar is on
        context.fillStyle = '#000000';
        
        if (isLeftSide) {
          // For left bars (opportunities), position risk number on the right side of the bar
          context.textAlign = 'left';
          context.fillText(riskNumber, canvas.width / 2 + 10, currentY + barHeight / 2 + 4);
        } else {
          // For right bars (threats), position risk number on the left side of the bar
          context.textAlign = 'right';
          context.fillText(riskNumber, canvas.width / 2 - 10, currentY + barHeight / 2 + 4);
        }
        
        // Position risk title below the bar on the right side
        if (riskTitle) {
          context.fillStyle = '#018cf4'; // Blue color for title
          context.textAlign = 'left';
          // Use text wrapping for longer titles
          const titleMaxWidth = 200;
          const lineHeight = 14;
          // Position the title below the bar with enough offset to avoid overlapping with center line
          const titleX = canvas.width / 2 + 20; // Increased offset from center
          const titleY = currentY + barHeight + 15;
          const linesCount = wrapText(context, riskTitle, titleX, titleY, titleMaxWidth, lineHeight);
          
          // If we have multiple lines, add extra space for the next bar
          if (linesCount > 1) {
            currentY += (linesCount - 1) * lineHeight;
          }
        }
        
        currentY += barHeight + 25; // Increased vertical spacing to accommodate bottom labels
      });
      
      // Add footer
      context.fillStyle = '#000000';
      context.font = '10px Arial';
      context.textAlign = 'center';
      
      // Convert canvas to PNG
      const imgData = canvas.toDataURL('image/png');
      
      // Create a download link
      const downloadLink = document.createElement('a');
      downloadLink.href = imgData;
      downloadLink.download = `${chartTitle}.png`;
      downloadLink.style.display = 'none';
      
      // Add to document, click, and remove
      document.body.appendChild(downloadLink);
      console.log("Download link created for PNG");
      downloadLink.click();
      
      // Clean up
      setTimeout(() => {
        document.body.removeChild(downloadLink);
      }, 100);
      
    } catch (error) {
      console.error("Error in PNG export:", error);
      alert("Failed to generate PNG. Please try again or contact support.");
    }
  };

  // Helper function to download chart as PDF with pagination
  downloadChartAsPDF = async () => {
    try {
      console.log("Starting PDF export with pagination...");
      
      // Get chart title from props or use default
      const chartTitle = this.props.title || 'Tornado Chart';
      
      // Get chart data
      const { leftPopulationDataNew, rightPopulationDataNew } = this.getChartData();
      
      // Define maximum items per page for consistent sizing
      const ITEMS_PER_PAGE = 10; // Adjust this value based on desired density
      
      // Calculate max value for scaling (need this for consistent scaling across pages)
      let maxValue = 0;
      leftPopulationDataNew.forEach(item => {
        const value = Math.abs(parseFloat(item.value.replace(/,/g, '')));
        if (value > maxValue) maxValue = value;
      });
      rightPopulationDataNew.forEach(item => {
        const value = Math.abs(parseFloat(item.value.replace(/,/g, '')));
        if (value > maxValue) maxValue = value;
      });
      
      // Add some margin
      maxValue = maxValue * 1.1;
      
      // Check if this is an opportunity chart
      const isOpportunityChart = this.props.isOpportunity === true;
      
      // Combine all data for pagination
      const allData = [...rightPopulationDataNew, ...leftPopulationDataNew];
      
      // Sort combined data by absolute value (largest first)
      const sortedData = allData.sort((a, b) => {
        const absA = Math.abs(parseFloat(a.value.replace(/,/g, '')));
        const absB = Math.abs(parseFloat(b.value.replace(/,/g, '')));
        if (absA < absB) return 1;
        if (absA > absB) return -1;
        return 0;
      });
      
      // Calculate total pages needed
      const totalPages = Math.ceil(sortedData.length / ITEMS_PER_PAGE);
      console.log(`Total data points: ${sortedData.length}, creating ${totalPages} pages with ${ITEMS_PER_PAGE} items per page`);
      
      // Create PDF with appropriate orientation (landscape works better for tornado charts)
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });
      
      // Set PDF metadata
      pdf.setProperties({
        subject: 'Tornado Chart Export',
        author: 'Riskwell',
        keywords: 'tornado, chart, export',
        creator: 'Riskwell Application'
      });
      
      // Get page dimensions
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      // Current date for header
      const currentDate = new Date().toLocaleDateString();
      
      // Helper function to wrap text
      const wrapText = (context, text, x, y, maxWidth, lineHeight) => {
        const words = text.split(' ');
        let line = '';
        let lines = [];
        
        for(let n = 0; n < words.length; n++) {
          const testLine = line + words[n] + ' ';
          const metrics = context.measureText(testLine);
          const testWidth = metrics.width;
          
          if (testWidth > maxWidth && n > 0) {
            lines.push(line);
            line = words[n] + ' ';
          } else {
            line = testLine;
          }
        }
        
        lines.push(line);
        
        for(let i = 0; i < lines.length; i++) {
          context.fillText(lines[i], x, y + (i * lineHeight));
        }
        
        return lines.length;
      };
      
      // Function to render a single page of the chart
      const renderChartPage = (pageData, pageIndex, isOpportunityChart) => {
        // Create a canvas for this page
        const canvas = document.createElement('canvas');
        canvas.width = 1200; // Width consistent across all pages
        
        // Calculate height based on number of items on this page
        const itemCount = pageData.length;
        const baseHeight = 150; // Base height for title, margins, etc.
        const minHeightPerItem = 60; // Consistent height per item
        const requiredHeight = baseHeight + (itemCount * minHeightPerItem);
        canvas.height = Math.max(625, requiredHeight);
        
        const context = canvas.getContext('2d');
        
        // Set background to white
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvas.width, canvas.height);
        
        // Draw title with page number
        context.fillStyle = '#000000';
        context.font = 'bold 24px Arial';
        context.textAlign = 'center';
        const updatedTitle = this.props.optionSelection == "all" ? chartTitle : chartTitle + ' - Top 10';
        context.fillText(`${updatedTitle}`, canvas.width / 2, 30);
        
        // Calculate scale factor - ensure bars are visible and consistent across pages
        const scaleX = Math.min((canvas.width / 2 - 150) / maxValue, 0.9);
        
        // Draw center line
        const barHeight = 30;
        const startY = 80;
        let currentY = startY;
        
        context.strokeStyle = '#999999';
        context.beginPath();
        context.moveTo(canvas.width / 2, 60);
        context.lineTo(canvas.width / 2, startY + (itemCount * (barHeight + 10) + 20));
        context.stroke();
        
        // Draw data for this page
        pageData.forEach((item, index) => {
          const value = Math.abs(parseFloat(item.value.replace(/,/g, '')));
          const barWidth = value * scaleX;
          const isPositive = parseFloat(item.value.replace(/,/g, '')) >= 0;
          
          // Determine if this item should be displayed on the left or right side
          // For opportunity charts, all values (including zero) should be on the left side
          // For threat charts, negative values on left, positive values on right
          let isLeftSide;
          
          if (isOpportunityChart) {
            // In opportunity charts, all values go on the left side
            isLeftSide = true;
          } else {
            // In threat charts, negative values go on left, positive on right
            isLeftSide = !isPositive;
          }
          
          // Draw bar with appropriate color
          // Yellow (#bfbf2d) for left side, Blue (#018cf4) for right side
          context.fillStyle = isLeftSide ? '#bfbf2d' : '#018cf4';
          const visibleBarWidth = Math.max(barWidth, 5);
          
          if (!isLeftSide) {
            // Draw from center to right
            context.fillRect(canvas.width / 2, currentY, visibleBarWidth, barHeight);
            
            // Draw value on the bar
            context.fillStyle = '#000000';
            context.font = '12px Arial';
            if (barWidth > 50) {
              context.textAlign = 'right';
              context.fillText(item.value, canvas.width / 2 + barWidth - 10, currentY + barHeight / 2 + 4);
            } else {
              context.textAlign = 'left';
              context.fillText(item.value, canvas.width / 2 + barWidth + 5, currentY + barHeight / 2 + 4);
            }
          } else {
            // Draw from center to left
            context.fillRect(canvas.width / 2 - visibleBarWidth, currentY, visibleBarWidth, barHeight);
            
            // Draw value on the bar
            context.fillStyle = '#000000';
            context.font = '12px Arial';
            if (barWidth > 50) {
              context.textAlign = 'left';
              context.fillText(item.value, canvas.width / 2 - barWidth + 10, currentY + barHeight / 2 + 4);
            } else {
              context.textAlign = 'right';
              context.fillText(item.value, canvas.width / 2 - barWidth - 5, currentY + barHeight / 2 + 4);
            }
          }
          
          // Extract risk number and title if available
          let riskNumber = '';
          let riskTitle = '';
          
          if (typeof item.riskNumber === 'string' && item.riskNumber.includes(' ')) {
            const parts = item.riskNumber.split(' ');
            riskNumber = parts[0];
            riskTitle = parts.slice(1).join(' ');
          } else {
            riskNumber = item.riskNumber || 'Item ' + index;
          }
          
          // Position risk number based on which side the bar is on
          context.fillStyle = '#000000';
          
          if (isLeftSide) {
            // For left bars (opportunities), position risk number on the right side of the bar
            context.textAlign = 'left';
            context.fillText(riskNumber, canvas.width / 2 + 10, currentY + barHeight / 2 + 4);
          } else {
            // For right bars (threats), position risk number on the left side of the bar
            context.textAlign = 'right';
            context.fillText(riskNumber, canvas.width / 2 - 10, currentY + barHeight / 2 + 4);
          }
          
          // Position risk title below the bar on the right side
          if (riskTitle) {
            context.fillStyle = '#018cf4'; // Blue color for title
            context.textAlign = 'left';
            // Use text wrapping for longer titles
            const titleMaxWidth = 200;
            const lineHeight = 14;
            // Position the title below the bar with enough offset to avoid overlapping with center line
            const titleX = canvas.width / 2 + 20; // Increased offset from center
            const titleY = currentY + barHeight + 15;
            const linesCount = wrapText(context, riskTitle, titleX, titleY, titleMaxWidth, lineHeight);
            
            // If we have multiple lines, add extra space for the next bar
            if (linesCount > 1) {
              currentY += (linesCount - 1) * lineHeight;
            }
          }
          
          currentY += barHeight + 25; // Increased vertical spacing to accommodate bottom labels
        });
        
        // Add page number at bottom
        context.fillStyle = '#000000';
        context.font = '10px Arial';
        context.textAlign = 'center';
        context.fillText(`Page ${pageIndex + 1} of ${totalPages}`, canvas.width / 2, canvas.height - 10);
        
        return canvas.toDataURL('image/png', 1.0);
      };
      
      // Generate each page
      for (let i = 0; i < totalPages; i++) {
        // If not the first page, add a new page to the PDF
        if (i > 0) {
          pdf.addPage();
        }
        
        // Get data for this page
        const startIdx = i * ITEMS_PER_PAGE;
        const endIdx = Math.min(startIdx + ITEMS_PER_PAGE, sortedData.length);
        const pageData = sortedData.slice(startIdx, endIdx);
        
        // Render chart for this page
        const imgData = renderChartPage(pageData, i, isOpportunityChart);
        
        // Add date to each page
        pdf.setFontSize(10);
        pdf.text(`Generated: ${currentDate}`, pdfWidth - 20, 10, { align: 'right' });
        
        // Calculate dimensions for the image
        const imgWidth = 1200; // Canvas width
        const imgHeight = Math.max(625, 150 + (pageData.length * 60)); // Canvas height
        
        // Use a larger portion of the page for the chart
        const maxWidth = pdfWidth * 0.9; // Use 90% of page width
        const maxHeight = pdfHeight * 0.8; // Use 80% of page height
        
        const ratio = Math.min(maxWidth / imgWidth, maxHeight / imgHeight);
        const scaledWidth = imgWidth * ratio;
        const scaledHeight = imgHeight * ratio;
        
        // Center the image
        const imgX = (pdfWidth - scaledWidth) / 2;
        const imgY = 25; // Position below the title
        
        // Add the chart image
        pdf.addImage(imgData, 'PNG', imgX, imgY, scaledWidth, scaledHeight);
      }
      
      console.log(`PDF created with ${totalPages} pages, attempting to download...`);
      
      // Save the PDF
      pdf.save(`${chartTitle}.pdf`);
      
    } catch (error) {
      console.error('Error in PDF export process:', error);
      alert("Failed to generate PDF. Please try again or contact support.");
    }
  };
  
  // Helper method to get chart data for export
  getChartData = () => {
    const tornodoResponse = this.props.tornodoResponse ? this.props.tornodoResponse : [];
    // Make a deep copy of the response to avoid modifying the original data
    const tornodoResponseCopy = JSON.parse(JSON.stringify(tornodoResponse));
    let rightPopulationData = [];
    let leftPopulationData = [];
    let rightPopulationDataNew = [];
    let leftPopulationDataNew = [];
    
    // Process data for left and right sides
    for (let row of Object.values(tornodoResponseCopy)) {
      if (typeof row.riskNumber === 'string') {
        if (this.props.isOpportunity == true) {
          row["percentage"] = Math.abs(row.percentage);
          row["riskNumber"] = row.riskNumber ? row.riskNumber.toString().split('##')[0] + " " + row.riskNumber.toString().split('##')[1] : row.riskNumber;
          leftPopulationData.push(row);
        } else {
          row["percentage"] = Math.abs(row.percentage);
          row["riskNumber"] = row.riskNumber ? row.riskNumber.toString().split('##')[0] + " " + row.riskNumber.toString().split('##')[1] : row.riskNumber;
          rightPopulationData.push(row);
        }
      } else {
        if (this.props.isOpportunity == true) {
          leftPopulationData.push(row);
        } else {
          rightPopulationData.push(row);
        }
      }
    }
    
    if (this.props.nutral == true) {
      rightPopulationData = [];
      leftPopulationData = [];
      for (let row of Object.values(tornodoResponseCopy)) {
        if (row.value < 0) {
          row["percentage"] = Math.abs(row.percentage);
          leftPopulationData.push(row);
        } else {
          row["percentage"] = Math.abs(row.percentage);
          rightPopulationData.push(row);
        }
      }
    }
    
    // Sort both right and left data in descending order by absolute value
    rightPopulationData = rightPopulationData.sort((a, b) => {
      const absA = Math.abs(parseFloat(a.value) || 0);
      const absB = Math.abs(parseFloat(b.value) || 0);
      if (absA < absB) return 1;
      if (absA > absB) return -1;
      return 0;
    });
    
    leftPopulationData = leftPopulationData.sort((a, b) => {
      const absA = Math.abs(parseFloat(a.value) || 0);
      const absB = Math.abs(parseFloat(b.value) || 0);
      if (absA < absB) return 1;
      if (absA > absB) return -1;
      return 0;
    });
    
    // Format values
    for (let row of Object.values(rightPopulationData)) {
      row["value"] = formatNumber(parseFloat(row.value), "##,#");
      rightPopulationDataNew.push(row);
    }
    
    for (let row of Object.values(leftPopulationData)) {
      row["value"] = formatNumber(parseFloat(row.value), "##,#");
      leftPopulationDataNew.push(row);
    }
    
    return { leftPopulationDataNew, rightPopulationDataNew };
  };

  // Method to export chart as PNG
  onImageExportClick = () => {
    this.downloadChartAsImage();
  };

  // Method to export chart as PDF
  onPDFExportClick = () => {
    this.downloadChartAsPDF();
  };
  
  // Method to export chart as email
  onEmailExportClick = async (emailId, message) => {
    try {
      console.log("Starting email export...");
      
      // Get chart title from props or use default
      const chartTitle = this.props.title || 'Tornado Chart';
      
      // Get chart data
      const { leftPopulationDataNew, rightPopulationDataNew } = this.getChartData();
      
      // Check if this is an opportunity chart
      const isOpportunityChart = this.props.isOpportunity === true;
      
      // Create a canvas element with increased width from the start
      const canvas = document.createElement('canvas');
      canvas.width = 1200; // Increased width to accommodate labels
      
      // Prepare data for rendering
      let combinedData = [];
      
      // Process left data (threats or all data for opportunity chart)
      const leftPopulationDataProcessed = leftPopulationDataNew.map(item => ({
        ...item,
        side: 'left',
        value: item.value.replace(/,/g, ''),
        absValue: Math.abs(parseFloat(item.value.replace(/,/g, '')))
      }));
      
      // Process right data (opportunities)
      const rightPopulationDataProcessed = rightPopulationDataNew.map(item => ({
        ...item,
        side: 'right',
        value: item.value.replace(/,/g, ''),
        absValue: Math.abs(parseFloat(item.value.replace(/,/g, '')))
      }));
      
      // For opportunity charts, all data goes on the left side
      combinedData = [...leftPopulationDataProcessed, ...rightPopulationDataProcessed];
      
      // Sort combined data by absolute value (largest first)
      combinedData.sort((a, b) => {
        if (a.absValue < b.absValue) return 1;
        if (a.absValue > b.absValue) return -1;
        return 0;
      });
      
      // Calculate the minimum required height based on the number of data points
      const totalDataPoints = combinedData.length;
      const baseHeight = 150; // Base height for title, margins, etc.
      const minHeightPerItem = 60; // Minimum height needed per data point
      const requiredHeight = baseHeight + (totalDataPoints * minHeightPerItem);
      
      // Use either the required height or a minimum of 625px, whichever is larger
      canvas.height = Math.max(625, requiredHeight);
      
      console.log(`Email canvas height set to ${canvas.height}px for ${totalDataPoints} data points`);
      const context = canvas.getContext('2d');
      
      // Set background to white for better visibility in exports
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw title
      context.fillStyle = '#000000';
      context.font = 'bold 24px Arial';
      context.textAlign = 'center';
      context.fillText(chartTitle, canvas.width / 2, 30);
      
      // Calculate max value for scaling
      const maxValue = Math.max(...combinedData.map(item => item.absValue)) * 1.1;
      
      // Calculate scale factor - ensure bars are visible
      const scaleX = Math.min((canvas.width / 2 - 150) / maxValue, 0.9);
      
      // Draw center line
      const barHeight = 30;
      const startY = 80;
      let currentY = startY;
      
      context.strokeStyle = '#999999';
      context.beginPath();
      context.moveTo(canvas.width / 2, 60);
      context.lineTo(canvas.width / 2, startY + (totalDataPoints * (barHeight + 10) + 20));
      context.stroke();
      
      // Draw axis labels
      context.fillStyle = '#000000';
      context.font = '12px Arial';
      context.textAlign = 'center';
      
      // Helper function to wrap text
      const wrapText = (context, text, x, y, maxWidth, lineHeight) => {
        const words = text.split(' ');
        let line = '';
        let lines = [];
        
        for(let n = 0; n < words.length; n++) {
          const testLine = line + words[n] + ' ';
          const metrics = context.measureText(testLine);
          const testWidth = metrics.width;
          
          if (testWidth > maxWidth && n > 0) {
            lines.push(line);
            line = words[n] + ' ';
          } else {
            line = testLine;
          }
        }
        
        lines.push(line);
        
        for(let i = 0; i < lines.length; i++) {
          context.fillText(lines[i], x, y + (i * lineHeight));
        }
        
        return lines.length;
      };
      
      // Process each data item
      combinedData.forEach((item, index) => {
        const value = item.absValue;
        const barWidth = value * scaleX;
        const isPositive = parseFloat(item.value) >= 0;
        
        // Extract risk number and title if available
        let riskNumber = '';
        let riskTitle = '';
        
        if (typeof item.riskNumber === 'string' && item.riskNumber.includes(' ')) {
          const parts = item.riskNumber.split(' ');
          riskNumber = parts[0];
          riskTitle = parts.slice(1).join(' ');
        } else {
          riskNumber = item.riskNumber || 'Item ' + index;
        }
        
        // Determine if this item should be displayed on the left or right side
        // For opportunity charts, all values (including zero) should be on the left side
        // For threat charts, negative values on left, positive values on right
        let isLeftSide;
        
        if (isOpportunityChart) {
          // In opportunity charts, all values go on the left side
          isLeftSide = true;
        } else {
          // In threat charts, negative values go on left, positive on right
          isLeftSide = !isPositive;
        }
        
        // Draw bar with appropriate color
        // Yellow (#bfbf2d) for left side, Blue (#018cf4) for right side
        context.fillStyle = isLeftSide ? '#bfbf2d' : '#018cf4';
        const visibleBarWidth = Math.max(barWidth, 5);
        
        if (!isLeftSide) {
          // Draw from center to right
          context.fillRect(canvas.width / 2, currentY, visibleBarWidth, barHeight);
          
          // Draw value on the bar
          context.fillStyle = '#000000';
          context.font = '12px Arial';
          if (barWidth > 50) {
            context.textAlign = 'right';
            context.fillText(item.value, canvas.width / 2 + barWidth - 10, currentY + barHeight / 2 + 4);
          } else {
            context.textAlign = 'left';
            context.fillText(item.value, canvas.width / 2 + barWidth + 5, currentY + barHeight / 2 + 4);
          }
          
          // Position risk number on the left side of the bar
          context.fillStyle = '#000000';
          context.textAlign = 'right';
          context.fillText(riskNumber, canvas.width / 2 - 10, currentY + barHeight / 2 + 4);
          
          // Position risk title below the bar
          if (riskTitle) {
            context.fillStyle = '#018cf4'; // Blue color for title
            context.textAlign = 'left';
            // Use text wrapping for longer titles
            const titleMaxWidth = 200;
            const lineHeight = 14;
            // Position the title below the bar with enough offset to avoid overlapping with center line
            const titleX = canvas.width / 2 + 20; // Increased offset from center
            const titleY = currentY + barHeight + 15;
            const linesCount = wrapText(context, riskTitle, titleX, titleY, titleMaxWidth, lineHeight);
            
            // If we have multiple lines, add extra space for the next bar
            if (linesCount > 1) {
              currentY += (linesCount - 1) * lineHeight;
            }
          }
        } else {
          // Draw from center to left
          context.fillRect(canvas.width / 2 - visibleBarWidth, currentY, visibleBarWidth, barHeight);
          
          // Draw value on the bar
          context.fillStyle = '#000000';
          context.font = '12px Arial';
          if (barWidth > 50) {
            context.textAlign = 'left';
            context.fillText(item.value, canvas.width / 2 - barWidth + 10, currentY + barHeight / 2 + 4);
          } else {
            context.textAlign = 'right';
            context.fillText(item.value, canvas.width / 2 - barWidth - 5, currentY + barHeight / 2 + 4);
          }
          
          // Position risk number on the right side of the bar
          context.fillStyle = '#000000';
          context.textAlign = 'left';
          context.fillText(riskNumber, canvas.width / 2 + 10, currentY + barHeight / 2 + 4);
          
          // Position risk title below the bar
          if (riskTitle) {
            context.fillStyle = '#018cf4'; // Blue color for title
            context.textAlign = 'left';
            // Use text wrapping for longer titles
            const titleMaxWidth = 200;
            const lineHeight = 14;
            // Position the title below the bar with enough offset to avoid overlapping with center line
            const titleX = canvas.width / 2 + 20; // Increased offset from center
            const titleY = currentY + barHeight + 15;
            const linesCount = wrapText(context, riskTitle, titleX, titleY, titleMaxWidth, lineHeight);
            
            // If we have multiple lines, add extra space for the next bar
            if (linesCount > 1) {
              currentY += (linesCount - 1) * lineHeight;
            }
          }
        }
        
        currentY += barHeight + 25; // Increased vertical spacing to accommodate bottom labels
      });
      
      // Convert canvas to image for email
      const imgData = canvas.toDataURL('image/png', 1.0);
      
      // Send email using EmailService
      const result = await EmailService.sendChartEmail(
        imgData,
        0, // widgetId
        emailId,
        message,
        `${chartTitle}.png`
      );
      
      if (result) {
        console.log('Email sent successfully');
      }
    } catch (error) {
      console.error('Error sending email:', error);
    }
  };

  render() {
    const tornodoResponse = this.props.tornodoResponse ? this.props.tornodoResponse : [];
    // Make a deep copy of the response to avoid modifying the original data
    const tornodoResponseCopy = JSON.parse(JSON.stringify(tornodoResponse));
    let rightPopulationData = []; 
    let leftPopulationData=[];
    let rightPopulationDataNew = [];
    let leftPopulationDataNew=[];
      for (let row of Object.values(tornodoResponseCopy)) {
        if( typeof row.riskNumber === 'string'){
            if (this.props.isOpportunity==true) {
              row["percentage"]=Math.abs(row.percentage);
              row["riskNumber"] = <div className='butterflyLabel'><span title="Risk number">{row.riskNumber? row.riskNumber.toString().split('##')[0]:row.riskNumber}</span> <span  className='txtBlue' title="Risk Title">{row.riskNumber? row.riskNumber.toString().split('##')[1]:row.riskNumber}</span></div>
              leftPopulationData.push(row);
            } else  {
              row["percentage"]=Math.abs(row.percentage);
              row["riskNumber"] = <div className='butterflyLabel'><span title="Risk number">{row.riskNumber? row.riskNumber.toString().split('##')[0]:row.riskNumber}</span> <span  className='txtBlue' title="Risk Title">{row.riskNumber? row.riskNumber.toString().split('##')[1]:row.riskNumber}</span></div>
              rightPopulationData.push(row);
            }
        }else{
          if (this.props.isOpportunity==true) {
            // row["percentage"]=Math.abs(row.percentage);
            // row["riskNumber"] = <div className='butterflyLabel'><span title="Risk number">{row.riskNumber? row.riskNumber.toString().split('##')[0]:row.riskNumber}</span> <span  className='txtBlue' title="Risk Title">{row.riskNumber? row.riskNumber.toString().split('##')[1]:row.riskNumber}</span></div>
            leftPopulationData.push(row);
          } else  {
            // row["percentage"]=Math.abs(row.percentage);
            // row["riskNumber"] = <div className='butterflyLabel'><span title="Risk number">{row.riskNumber? row.riskNumber.toString().split('##')[0]:row.riskNumber}</span> <span  className='txtBlue' title="Risk Title">{row.riskNumber? row.riskNumber.toString().split('##')[1]:row.riskNumber}</span></div>
            rightPopulationData.push(row);
          }
        }
    }

    if(this.props.nutral==true){
      rightPopulationData=[];
      leftPopulationData=[];
      for (let row of Object.values(tornodoResponseCopy)) {
        if (row.value<0) {
          // row["value"]=formatNumber(parseFloat(row.value), "##,#");
          row["percentage"]=Math.abs(row.percentage);
          leftPopulationData.push(row);
        } else  {
          // row["value"]=formatNumber(parseFloat(row.value), "##,#");
          row["percentage"]=Math.abs(row.percentage);
          rightPopulationData.push(row);
        }
    }
    }

     // Sort both right and left data in descending order by absolute value
     // This ensures the most significant values (largest absolute values) are shown first
     rightPopulationData = rightPopulationData.sort((a, b) => {
      const absA = Math.abs(parseFloat(a.value) || 0);
      const absB = Math.abs(parseFloat(b.value) || 0);
      if (absA < absB) return 1;
      if (absA > absB) return -1;
      return 0;
    });
    leftPopulationData = leftPopulationData.sort((a, b) => {
      const absA = Math.abs(parseFloat(a.value) || 0);
      const absB = Math.abs(parseFloat(b.value) || 0);
      if (absA < absB) return 1;
      if (absA > absB) return -1;
      return 0;
    });

    for (let row of Object.values(rightPopulationData)){
      row["value"]=formatNumber(parseFloat(row.value), "##,#");
      rightPopulationDataNew.push(row);
    }
   
    for (let row of Object.values(leftPopulationData)){
      row["value"]=formatNumber(parseFloat(row.value), "##,#");
      leftPopulationDataNew.push(row);
    }

    return (
      <div className='butterflyChartCont'>
        <ul className="WidgetActions">
          <li title="PNG Download" onClick={this.onImageExportClick}>
            <a>
              <img src={Images.DOWNLOAD} alt="Download" width="14" />
            </a>
          </li>
          <li title="PDF Download" onClick={this.onPDFExportClick}>
            <a>
              <img src={Images.PDF} alt="Download" width="14" />
            </a>
          </li>
          <li>
            <EmailChartPopup
              onSendEmail={this.onEmailExportClick}
              widgetId={0}
              widgetName={this.props.title || 'Tornado Chart'}
            />
          </li>
        </ul>
        <div ref={this.chartContainerRef}>
          <ButterflyChart
            ref={this.butterflyChartRef}
            titleLeft=""
            titleRight=""
            leftData={leftPopulationDataNew}
            rightData={rightPopulationDataNew}
            // rightData={tornodoResponse}
            labelField="riskNumber"
            dataField="value"
            // dataField="percentage"
            leftColor="#bfbf2d"
            rightColor="#018cf4"
          />
        </div>
      </div>)
  }
}
